/* eslint-disable react/no-danger */
import classNames from 'classnames';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import ChevronIcon from '../icons/Chevron';

import styles from '../../../styles/partials/accordion.module.scss';

const propTypes = {
    className: PropTypes.string,
    innerClassName: PropTypes.string,
    titleClassName: PropTypes.string,
    buttonClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    iconClassName: PropTypes.string,
    title: PropTypes.string,
    children: PropTypes.node,
    icon: PropTypes.oneOf(['arrow', 'plus']),
    withoutCollapse: PropTypes.bool,
};

const defaultProps = {
    className: null,
    innerClassName: null,
    labelClassName: null,
    buttonClassName: null,
    contentClassName: null,
    iconClassName: null,
    label: null,
    children: null,
    icon: null,
    withoutCollapse: false,
};

function Accordion({
    className,
    innerClassName,
    labelClassName,
    buttonClassName,
    contentClassName,
    iconClassName,
    label,
    children,
    icon,
    withoutCollapse,
}) {
    const themeClassName = useThemeClassName(styles);
    const [opened, setOpened] = useState(false);

    const panelRef = useRef(null);
    const panelHeight = opened ? panelRef.current.scrollHeight : null;

    const toggleOpen = useCallback(() => {
        setOpened((old) => !old);
    }, []);

    const content = (
        <>
            {isString(label) ? (
                <span
                    className={classNames([
                        styles.label,
                        { [labelClassName]: labelClassName !== null },
                    ])}
                    dangerouslySetInnerHTML={{ __html: label }}
                />
            ) : (
                label
            )}
            {!withoutCollapse ? (
                <span className={styles.iconContainer}>
                    {icon === 'plus' ? (
                        <div
                            className={classNames([
                                styles.icon,
                                styles.plusMinus,
                                { [iconClassName]: iconClassName !== null },
                            ])}
                        />
                    ) : (
                        <ChevronIcon
                            direction={opened ? 'up' : 'down'}
                            className={classNames([
                                styles.icon,
                                { [iconClassName]: iconClassName !== null },
                            ])}
                        />
                    )}
                </span>
            ) : null}
        </>
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.opened]: opened,
                    [styles.withoutCollapse]: withoutCollapse,
                },
                themeClassName,
                className,
            ])}
        >
            <div
                className={classNames([
                    styles.inner,
                    { [innerClassName]: innerClassName !== null },
                ])}
            >
                {withoutCollapse ? (
                    <div className={classNames([styles.button, buttonClassName])}>{content}</div>
                ) : (
                    <Button
                        className={classNames([styles.button, buttonClassName])}
                        onClick={toggleOpen}
                    >
                        {content}
                    </Button>
                )}
                <div className={styles.panel} style={{ maxHeight: panelHeight }} ref={panelRef}>
                    <div className={classNames([styles.content, contentClassName])}>{children}</div>
                </div>
            </div>
        </div>
    );
}

Accordion.propTypes = propTypes;
Accordion.defaultProps = defaultProps;

export default Accordion;
